import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import api from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/api/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (
      typeof window !== "undefined" &&
      window.location.hash &&
      window.location.hash.includes("session_id=")
    ) {
      setIsLoading(false);
      return;
    }
    (async () => {
      await refresh();
      setIsLoading(false);
    })();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/api/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const signup = useCallback(async (email, password, name) => {
    const { data } = await api.post("/api/auth/signup", { email, password, name });
    setUser(data);
    return data;
  }, []);

  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  }, []);

  const completeGoogleCallback = useCallback(async (sessionId) => {
    const { data } = await api.post("/api/auth/google/callback", {
      session_id: sessionId,
    });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    // Navigate to landing FIRST so RequireAuth doesn't bounce us to /login
    // during the brief tick when user has been cleared but route hasn't changed.
    navigate("/", { replace: true });
    try {
      await api.post("/api/auth/logout");
    } catch {
      /* swallow */
    }
    setUser(null);
  }, [navigate]);

  const value = useMemo(
    () => ({
      user,
      isLoading,
      isAuthenticated: !!user,
      login,
      signup,
      loginWithGoogle,
      completeGoogleCallback,
      logout,
      refresh,
    }),
    [user, isLoading, login, signup, loginWithGoogle, completeGoogleCallback, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
